import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import ListFilterBar from '../components/work/ListFilterBar';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ErrorAlert, ListEmpty, LoadingRows, PageTitle } from '../components/work/page-states';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import LoadBar from '../components/work/LoadBar';
import TeamCalendar from '../components/work/TeamCalendar';
import { apiFetch, v1Path } from '../lib/api';
import { matchesQuery, selectedAllows } from '../lib/directory-filter';
import { forecastCapacity, nextFreeCopy } from '../lib/capacity-forecast';
import {
  loadBandLabel,
  resourceDisplayName,
  type LoadBand,
  type ResourceRow,
  type ResourcesOverview,
} from '../lib/resource-types';
import { UserAvatar } from '../components/UserAvatar';

const RESOURCE_CHIPS = ['band'] as const;
const BAND_OPTIONS: Array<{ value: LoadBand; label: string }> = [
  { value: 'overloaded', label: 'Overloaded' },
  { value: 'high', label: 'High' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'available', label: 'Available' },
];

export default function Resources() {
  const filters = useDirectoryQuery(RESOURCE_CHIPS, null);
  const [data, setData] = useState<ResourcesOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'calendar' | 'overview'>('calendar');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiFetch<ResourcesOverview>(v1Path('/resources')));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.resources.filter((row) => {
      if (!selectedAllows(filters.selected.band, row.load_band)) return false;
      return matchesQuery(filters.query, [resourceDisplayName(row), row.email]);
    });
  }, [data, filters.query, filters.selected.band]);

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
        <PageTitle
          title="People"
          count={data ? rows.length : undefined}
        />
        {error ? <ErrorAlert>{error}</ErrorAlert> : null}
        {loading ? <LoadingRows columns={4} /> : null}
        {!loading && data ? (
          <>
            <p className="text-sm">
              <span className="font-medium tabular-nums">{data.summary.resource_count}</span>
              {' people · '}
              <span className="font-medium tabular-nums">{data.summary.loaded_percent}%</span>
              {' loaded · '}
              <span className="font-medium tabular-nums">{data.summary.overloaded_count}</span>
              {' overloaded · '}
              <span className="font-medium tabular-nums">{data.summary.available_count}</span>
              {' available'}
            </p>
            <ListFilterBar
              query={filters.query}
              onQueryChange={(query) => filters.apply({ query })}
              searchPlaceholder="Search people..."
              searchAriaLabel="Search team"
              chips={[
                {
                  id: 'band',
                  label: 'Load',
                  icon: Activity,
                  options: BAND_OPTIONS,
                  selected: filters.selected.band,
                  onToggle: (value) => filters.toggleChip('band', value),
                },
              ]}
              trailing={
                <div className="flex items-center gap-1">
                  {(['calendar', 'overview'] as const).map((id) => (
                    <Button
                      key={id}
                      type="button"
                      size="sm"
                      variant={tab === id ? 'secondary' : 'ghost'}
                      className="h-8 capitalize"
                      onClick={() => setTab(id)}
                    >
                      {id}
                    </Button>
                  ))}
                </div>
              }
              onClear={filters.clear}
            />
            {data && data.resources.length === 0 ? (
              <BrandEmpty title="No people yet." />
            ) : rows.length === 0 ? (
              <ListEmpty title="No people match." />
            ) : tab === 'calendar' ? (
              <TeamCalendar rows={rows} />
            ) : (
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-0 w-[22%]">Name</TableHead>
                    <TableHead className="w-[14rem]">Load</TableHead>
                    <TableHead className="w-[12rem]">Can take work</TableHead>
                    <TableHead className="min-w-0">Currently working</TableHead>
                    <TableHead className="min-w-0 w-[22%]">Next</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.user_id}>
                      <TableCell className="align-top">
                        <div className="flex items-start gap-2.5">
                          <UserAvatar name={resourceDisplayName(row)} src={row.avatar_url} size={32} />
                          <div>
                            <Link to={`/resources/${row.user_id}`} className="font-medium hover:underline">
                              {resourceDisplayName(row)}
                            </Link>
                            <PersonCounts items={row.items} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <LoadBar percent={row.load_percent} band={row.load_band} />
                        <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                          {loadBandLabel(row.load_band)} · {row.allocated_hours}h of {row.weekly_capacity_hours}h this week
                        </p>
                        {row.unestimated_count > 0 ? (
                          <p className="text-xs text-muted-foreground">
                            {row.unestimated_count} {row.unestimated_count === 1 ? 'task has' : 'tasks have'} no time estimate
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="align-top text-xs">{freeCopy(row)}</TableCell>
                      <TableCell className="align-top text-xs">
                        {row.current ? (
                          <WorkBits
                            keyLabel={row.current.key}
                            title={row.current.title}
                            projectId={row.current.project_id}
                            workItemId={row.current.id}
                          />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {row.items
                          .filter((item) => item.id !== row.current?.id && item.state_group === 'started')
                          .slice(0, 2)
                          .map((item) => (
                            <p key={item.id} className="mt-1 text-muted-foreground">
                              {item.key} {item.title}
                            </p>
                          ))}
                      </TableCell>
                      <TableCell className="align-top text-xs">
                        {row.next ? (
                          <WorkBits
                            keyLabel={row.next.key}
                            title={row.next.title}
                            projectId={row.next.project_id}
                            workItemId={row.next.id}
                          />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        ) : null}
      </div>
    </PageFrame>
  );
}

function PersonCounts({ items }: { items: ResourceRow['items'] }) {
  const open = items.filter((item) => item.state_group !== 'completed' && item.state_group !== 'cancelled');
  const waiting = open.filter((item) => item.blocked).length;
  return (
    <p className="text-xs text-muted-foreground">
      <span className="tabular-nums">{open.length}</span> open
      {waiting > 0 ? (
        <>
          {' · '}
          <span className="font-medium text-amber-700 dark:text-amber-400">
            <span className="tabular-nums">{waiting}</span> waiting
          </span>
        </>
      ) : null}
    </p>
  );
}

function freeCopy(row: { allocated_hours: number; weekly_capacity_hours: number }): string {
  if (row.weekly_capacity_hours <= 0) return 'No weekly capacity set';
  return nextFreeCopy(forecastCapacity(row.allocated_hours, row.weekly_capacity_hours, new Date()));
}

function WorkBits({
  keyLabel,
  title,
  projectId,
  workItemId,
}: {
  keyLabel: string;
  title: string;
  projectId: string;
  workItemId: string;
}) {
  return (
    <Link to={`/projects/${projectId}/work-items/${workItemId}`} className="hover:underline">
      <span className="font-medium">{keyLabel}</span> {title}
    </Link>
  );
}
